import { Control, FieldValues } from 'react-hook-form';
import { Chip, ColourVariant, Typography } from '@weareopenr/component-library';
import { FormAutocomplete } from 'src/components/FormElements';
import { IMenu } from 'src/types/menu.types';
import { IMenuSet } from 'src/api/getMenuSets';
import { IPriceScheme } from 'src/types/price.types';
import styles from './TimetableDefaultsFields.module.scss';

type MenuOptionKind = 'menu' | 'menuSet';

const MENU_OPTION_TAGS: Record<MenuOptionKind, { label: string; colour: ColourVariant }> = {
  menu: { label: 'Menu', colour: 'blueberry' },
  menuSet: { label: 'Menu set', colour: 'grape' },
};

const MenuOptionRow = ({ kind, name }: { kind: MenuOptionKind; name: string }) => (
  <span className={styles['menu-option']}>
    <Chip label={MENU_OPTION_TAGS[kind].label} backgroundColour={MENU_OPTION_TAGS[kind].colour} />
    <span className={styles['menu-option-name']} title={name}>
      {name}
    </span>
  </span>
);

interface TimetableDefaultsFieldsProps<
  T extends FieldValues & { defaultMenuId: string; defaultPriceSchemeId: string },
> {
  control: Control<T>;
  menus: IMenu[];
  // Offered alongside menus - a timetable's default menu can be a menu OR a menu set.
  menuSets?: IMenuSet[];
  isMenusLoading: boolean;
  priceSchemes: IPriceScheme[];
  isPriceSchemesLoading: boolean;
  // Only relevant while creating a timetable - explains what a default does to someone who
  // hasn't built one yet, so it's skipped when editing an existing timetable's details.
  showDescription?: boolean;
}

export const TimetableDefaultsFields = <
  T extends FieldValues & { defaultMenuId: string; defaultPriceSchemeId: string },
>({
  control,
  menus,
  menuSets = [],
  isMenusLoading,
  priceSchemes,
  isPriceSchemesLoading,
  showDescription = true,
}: TimetableDefaultsFieldsProps<T>) => {
  // Menus and menu sets share one A-Z list, each tagged with what it is - unless there are no menu
  // sets, where a "Menu" tag on every row would say nothing.
  const isMixed = menuSets.length > 0;
  const toMenuOption = (id: string, name: string, kind: MenuOptionKind) => ({
    id,
    name,
    ...(isMixed && { content: <MenuOptionRow kind={kind} name={name} /> }),
  });
  const menuOptions = [
    ...menus.map((menu) => toMenuOption(menu.menuId, menu.name, 'menu')),
    ...menuSets.map((menuSet) => toMenuOption(menuSet.menu_set_id, menuSet.name, 'menuSet')),
  ];
  if (isMixed) menuOptions.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const priceSchemeOptions = priceSchemes.map((scheme) => ({ id: scheme.id, name: scheme.name }));

  return (
    <div className={styles['defaults-section']}>
      <div className={styles['defaults-section-sub']}>
        <Typography size="medium" weight="bold">
          Define the Defaults:
        </Typography>
        {showDescription && (
          <Typography size="xsmall">
            Default Menus and Price schemes will cover the full weekly timetable, allowing you to quickly build on top.
          </Typography>
        )}
      </div>

      {/* react-hook-form can't prove a generic T satisfies Path<T> from an intersection
          constraint alone - narrow control to the one field each FormAutocomplete touches. */}
      <FormAutocomplete
        name="defaultMenuId"
        control={control as unknown as Control<{ defaultMenuId: string }>}
        label="Default Menu"
        options={menuOptions}
        disabled={isMenusLoading}
        rules={{ required: 'Please select a default menu' }}
        className={styles['default-menu-field']}
      />

      <FormAutocomplete
        name="defaultPriceSchemeId"
        control={control as unknown as Control<{ defaultPriceSchemeId: string }>}
        label="Default Price Scheme"
        options={priceSchemeOptions}
        disabled={isPriceSchemesLoading}
        rules={{ required: 'Please select a default price scheme' }}
        forceBottomPlacement
      />
    </div>
  );
};

export default TimetableDefaultsFields;
